import type { NewsArticle } from "@/content/types";

export const article: NewsArticle = {
  slug: "bankrekening-verificatie-casino",
  title: "Strengere bankrekeningcontrole bij casino's",
  description:
    "Nieuwe guidance van 8 oktober 2026: casino's moeten je bankrekening en voornaam nauwkeuriger controleren. Dit merk je bij je eerste storting. 18+.",
  category: "Regelgeving",
  publishedAt: "2026-10-16",
  updatedAt: "2026-10-16",
  publishedLabel: "16 oktober 2026",
  authorSlug: "naomi-bootman",
  reviewerSlug: "wietse-van-koppen",
  readingMinutes: 8,
  ogName: "bankrekening-verificatie",
  heroImage: "bankPayment",
  heroMethods: ["ideal", "trustly"],
  heroAlt: "Controle van je bankrekening bij een eerste casinostorting met iDEAL en Trustly",
  linkPhrases: ["nieuwe regels voor bankrekeningverificatie", "controle van je bankrekening"],
  blocks: [
    {
      type: "p",
      text: "Op 8 oktober 2026 publiceerde de Nederlandse kansspeltoezichthouder nieuwe guidance over één onderdeel van het aanmelden dat de meeste spelers nauwelijks opmerken: de controle van je bankrekening. Het klinkt technisch. Toch is het precies het moment waarop een eerste storting soms vastloopt, of waarop een uitbetaling dagen langer duurt dan je verwachtte.",
    },
    {
      type: "p",
      text: "Wij lazen de publicatie, legden haar naast wat we zelf zien bij stortingen en zetten op een rij wat je als speler kunt verwachten. Kort gezegd: casino's moeten beter kijken van wie de rekening is. Voor de meeste mensen verandert er weinig. Voor een kleine groep wordt het merkbaar strenger.",
    },
    {
      type: "callout",
      title: "In het kort",
      text: "Gokbedrijven moeten de naam op je bankrekening nauwkeuriger vergelijken met de naam op je account, ook je voornaam. Jongerenrekeningen en rekeningen van iemand anders horen geweigerd te worden. Zakelijke rekeningen en andere betaalmethoden krijgen extra aandacht. 18+.",
    },

    { type: "p", text: "Kort daarna schreef de toezichthouder ook dat hij betalingen naar illegale casino's wil tegenhouden. Ook daar staat de controle van je betaalrekening centraal." },
    { type: "h2", text: "Waarom de toezichthouder nu ingrijpt" },
    {
      type: "p",
      text: "De aanleiding is ongemakkelijk. Volgens de toezichthouder kwamen er signalen binnen dat minderjarigen in sommige gevallen met hun eigen bankrekening konden storten. Dat mag niet: online gokken is in Nederland pas vanaf 18 jaar toegestaan.",
    },
    {
      type: "p",
      text: "Uit het onderzoek dat volgde bleek dat niet elk gokbedrijf rekeningen goed controleerde. Er kon gespeeld worden met jongerenrekeningen. En soms werd de bankrekening van een minderjarige gekoppeld aan het account van een volwassen speler. Dan klopt de leeftijd van het account, maar niet die van de persoon achter het geld.",
    },
    {
      type: "p",
      text: "De nieuwe guidance geeft gokbedrijven meer richting. Er staat onder meer in hoe ze voornamen moeten controleren, hoe ze omgaan met zakelijke rekeningen en wat er geldt voor andere betaalmethoden dan een gewone bankrekening.",
    },

    { type: "h2", text: "Wat er bij je eerste storting gebeurt" },
    {
      type: "p",
      text: "Bij de meeste Nederlandse aanbieders is je eerste storting ook je eerste controle. Je betaalt met iDEAL vanaf je eigen rekening, en de betaalverwerker geeft de naam van de rekeninghouder door. Het casino vergelijkt die naam met de naam waarmee je je hebt aangemeld.",
    },
    {
      type: "p",
      text: "Tot nu toe was dat bij sommige aanbieders een grove vergelijking: klopt de achternaam ongeveer, dan is het goed. De guidance vraagt om meer. Ook de voornaam telt mee. Staat er op je rekening een andere voornaam dan op je account, dan mag het casino de rekening niet zomaar accepteren.",
    },
    {
      type: "p",
      text: "In de praktijk merk je dat vooral als er iets afwijkt. Bijvoorbeeld als je account op \"Kees\" staat en je rekening op \"Cornelis\". Of als je een en/of-rekening met je partner gebruikt waarop jouw naam als tweede staat. Dan kan een aanbieder om extra bewijs vragen, zoals een bankafschrift.",
    },
    { type: "h3", text: "Hoe je vertraging voorkomt" },
    {
      type: "ul",
      items: [
        "Meld je aan met je officiële voornaam, zoals die ook op je bankpas en in je bankapp staat.",
        "Stort de eerste keer vanaf een rekening die alleen op jouw naam staat.",
        "Gebruik geen rekening van een partner, ouder of huisgenoot, ook niet 'even' voor één storting.",
        "Wil je toch een gezamenlijke rekening gebruiken? Vraag dan vooraf bij de klantenservice welk bewijs ze willen zien.",
      ],
    },

    { type: "h2", text: "Jongerenrekeningen en rekeningen van een ander" },
    {
      type: "p",
      text: "Het duidelijkste punt uit de publicatie: met een jongerenrekening mag je niet gokken. Dat lijkt vanzelfsprekend, maar een jongerenrekening ziet er voor een betaalverwerker soms hetzelfde uit als een gewone betaalrekening. Banken zetten zo'n rekening pas om als je 18 wordt, en niet altijd precies op je verjaardag.",
    },
    {
      type: "p",
      text: "Ben je net 18 en heb je nog een jongerenrekening, dan kan een storting dus geweigerd worden, ook al ben je wettelijk oud genoeg. Dat is geen fout van het casino. Je bank moet de rekening eerst omzetten.",
    },
    {
      type: "p",
      text: "Het koppelen van andermans rekening aan je account mag ook niet. Dat geldt in beide richtingen: een volwassene die de rekening van een kind gebruikt, maar ook iemand die voor een vriend stort. Het geld en het account moeten bij dezelfde persoon horen.",
    },

    { type: "h2", text: "Wat dit betekent per betaalmethode" },
    {
      type: "p",
      text: "De guidance gaat niet alleen over bankrekeningen. Ook andere betaalmethoden komen aan bod. Hoe de controle precies werkt, hangt af van welke gegevens een methode doorgeeft aan het casino.",
    },
    {
      type: "table",
      caption: "Welke naamgegevens het casino per methode ziet (globaal)",
      head: ["Methode", "Naam rekeninghouder zichtbaar?", "Wat je kunt verwachten"],
      rows: [
        ["iDEAL", "Ja, via de bank", "Naamcheck bij eerste storting, inclusief voornaam"],
        ["Trustly", "Ja, via je bankkoppeling", "Vergelijkbaar met iDEAL; uitbetaling naar dezelfde rekening"],
        ["Creditcard / debitkaart", "Gedeeltelijk, via kaartnaam", "Soms extra verificatie van de kaart"],
        ["E-wallet (Skrill, Neteller)", "Ja, naam van walletaccount", "Wallet moet op jouw naam staan"],
        ["Prepaid (Paysafecard)", "Nee", "Uitbetalen loopt via een geverifieerde bankrekening"],
      ],
    },
    {
      type: "p",
      text: "Bij Trustly werkt het vrijwel hetzelfde als bij iDEAL: je logt in bij je eigen bank en het casino krijgt de naam van de rekeninghouder te zien. Het voordeel is dat de uitbetaling daarna naar dezelfde gecontroleerde rekening kan teruglopen.",
    },
    {
      type: "p",
      text: "Prepaid betalen met Paysafecard geeft geen naam door. Daarom moet je bij een uitbetaling alsnog een bankrekening opgeven, en die wordt dan gecontroleerd. Wie met een e-wallet als Skrill speelt, moet ervoor zorgen dat de wallet op exact dezelfde naam staat als het casinoaccount.",
    },

    { type: "h2", text: "Zakelijke rekeningen: waarom ze vaker worden geweigerd" },
    {
      type: "p",
      text: "Een van de onderwerpen in de guidance is het gebruik van zakelijke rekeningen. Veel zzp'ers hebben een zakelijke rekening op hun eigen naam, soms met de bedrijfsnaam erbij. Voor een casino is dan lastiger te zien wie de eigenaar is.",
    },
    {
      type: "p",
      text: "Onze inschatting: verwacht dat zakelijke rekeningen vaker worden geweigerd of extra bewijs vragen. Het is simpeler om voor je speelgeld een privérekening te gebruiken. Dat houdt ook je administratie overzichtelijk, wat bij een belastingaangifte prettig is.",
    },

    { type: "h2", text: "Hoe het samenhangt met iDIN en registreren" },
    {
      type: "p",
      text: "De bankrekeningcontrole staat los van de identiteitscontrole bij registratie, maar ze raken elkaar wel. Bij aanbieders die iDIN gebruiken, bevestig je je naam, adres en geboortedatum via je bank. De naam die daaruit komt, sluit dan vaak precies aan op de naam van je rekening.",
    },
    {
      type: "p",
      text: "Registreer je zonder iDIN, bijvoorbeeld bij een casino zonder iDIN dat met een andere vorm van identificatie werkt, dan vult de bankrekeningcontrole een deel van het plaatje aan. Juist daar maakt een nauwkeurige naamvergelijking het verschil. Hoe die snelle registratie werkt en welke controles altijd blijven gelden, lees je in onze aparte gids.",
    },
    {
      type: "p",
      text: "Belangrijk om te weten: geen enkele legale aanbieder mag de leeftijds- en identiteitscontrole of de Cruks-check overslaan. De nieuwe guidance maakt dat alleen nauwkeuriger.",
    },

    { type: "h2", text: "Gevolgen voor je uitbetaling" },
    {
      type: "p",
      text: "Voor uitbetalingen is dit eigenlijk goed nieuws. Een casino betaalt winst uit naar een rekening die het al heeft gecontroleerd. Is die controle bij je eerste storting netjes gedaan, dan hoeft er bij de opname minder uitgezocht te worden.",
    },
    {
      type: "p",
      text: "In onze eigen metingen zien we dat de eerste opname bij een nieuw account het langst duurt. Daarna gaat het meestal sneller. Wie dus wil weten welke aanbieders vlot uitbetalen, kijkt het best naar onze pagina over casino's met snelle uitbetaling, waar we per aanbieder de doorlooptijd bijhouden.",
    },
    {
      type: "p",
      text: "Een afwijkende naam is overigens nog steeds de meest voorkomende reden dat een uitbetaling blijft hangen. Klopt je naam vanaf het begin, dan scheelt dat later gedoe.",
    },

    { type: "h2", text: "Wat wij gaan bijhouden" },
    {
      type: "p",
      text: "Een guidance is geen nieuwe wet. Het is een uitleg van regels die al bestonden, met de boodschap dat ze beter moeten worden nageleefd. Gokbedrijven passen hun processen daardoor geleidelijk aan, niet allemaal tegelijk.",
    },
    {
      type: "p",
      text: "Wij letten de komende weken bij onze stortingen op drie dingen: of de voornaamcontrole strenger wordt, of er vaker om een bankafschrift wordt gevraagd en of de eerste uitbetaling daardoor sneller of juist trager gaat. Wat we zien, verwerken we in de testdata op de betaalmethodepagina's, zoals die van iDEAL en Trustly, en in de casinozoeker.",
    },
    {
      type: "p",
      text: "Wil je zien welke betaalmethoden bij welke aanbieder werken, dan vind je die in het overzicht van alle betaalmethoden. Daar staat per methode ook of je ermee kunt uitbetalen.",
    },

    { type: "h2", text: "Drie voorbeelden uit de praktijk" },
    { type: "p", text: "Om het concreet te maken, drie situaties die we in onze eigen tests en in vragen van lezers vaak tegenkomen. Ze laten zien waar de nieuwe uitleg het verschil maakt." },
    { type: "h3", text: "Het verkorte voornaam-probleem" },
    { type: "p", text: "Lisa heet officieel Elisabeth. Haar casinoaccount staat op Lisa, haar bankrekening op E. de Vries. Tot voor kort ging dat bij sommige aanbieders gewoon goed: de achternaam klopte. Onder de nieuwe guidance hoort een aanbieder hier extra te kijken. De oplossing is simpel. Pas je accountnaam aan naar je officiële naam, of stuur een kopie van je bankpas of bankafschrift mee waarop je volledige naam staat." },
    { type: "h3", text: "De gedeelde rekening" },
    { type: "p", text: "Mark en Sanne hebben een en/of-rekening. Mark stort, maar de rekening staat in de bankgegevens als 'S. Jansen en/of M. Bakker'. Een aanbieder ziet dan twee namen, waarvan er één niet bij het account hoort. Dat kan prima geaccepteerd worden, maar reken op een vraag om bewijs. Wie dat gedoe wil vermijden, gebruikt een eigen privérekening." },
    { type: "h3", text: "Net 18 geworden" },
    { type: "p", text: "Daan is net 18 en wil een account openen. Zijn bank heeft zijn jongerenrekening nog niet omgezet. Het casino weigert de storting. Dat voelt onterecht, maar het klopt volgens de regels: de rekening is nog als jongerenrekening geregistreerd. Na het omzetten door de bank lukt het wel. Ons advies voor wie net 18 is: wacht ook even met spelen en lees eerst hoe stortlimieten werken. Jongvolwassenen hebben in Nederland lagere maandgrenzen voordat een aanbieder een extra controle moet doen." },
    { type: "h2", text: "De IBAN-Naam Check is iets anders" },
    { type: "p", text: "Verwar deze guidance niet met de IBAN-Naam Check. Die bestaat sinds oktober 2025 in heel Europa en controleert bij een overboeking of de naam en het IBAN van de ontvanger bij elkaar horen. Dat is een check die jij als betaler ziet, in je eigen bankapp." },
    { type: "p", text: "De nieuwe guidance gaat over de andere kant: het casino controleert of de rekening waarvan jij stort, echt van jou is. Beide controles werken samen. Ze maken het lastiger om met andermans geld te spelen en lastiger voor oplichters om geld om te leiden." },
    { type: "ul", items: ["IBAN-Naam Check: jouw bank controleert de ontvanger als jij geld overmaakt.", "Bankrekeningverificatie: het casino controleert of de betaalrekening op jouw naam staat.", "iDIN of een andere identiteitscheck: het casino controleert wie jij bent en hoe oud je bent."] },
    { type: "p", text: "Alle drie draaien ze om dezelfde vraag: is degene die betaalt ook degene die speelt? Hoe beter dat klopt, hoe soepeler storten en uitbetalen verlopen." },
    { type: "h2", text: "Veelgestelde vragen" },
    { type: "h3", text: "Moet ik iets doen als ik al een account heb?" },
    {
      type: "p",
      text: "Meestal niet. Is je rekening al geverifieerd en klopt de naam, dan merk je er niets van. Een aanbieder kan bestaande accounts wel opnieuw nalopen. Krijg je een verzoek om extra gegevens, reageer dan via het account zelf en niet via een link in een mail.",
    },
    { type: "h3", text: "Mijn storting werd geweigerd, wat nu?" },
    {
      type: "p",
      text: "Controleer of de naam op je rekening exact overeenkomt met je account, inclusief voornaam. Gebruik je een jongerenrekening, vraag je bank dan om de rekening om te zetten. Neem daarna contact op met de klantenservice van het casino.",
    },
    { type: "h3", text: "Geldt dit ook voor buitenlandse rekeningen?" },
    {
      type: "p",
      text: "De publicatie gaat over de verplichtingen van vergunde gokbedrijven in Nederland, ongeacht welke rekening je gebruikt. Bij een buitenlandse rekening, zoals bij sommige neobanken, kan de naamweergave afwijken. Houd dan rekening met extra vragen.",
    },
    {
      type: "callout",
      title: "Speel bewust",
      text: "Een snellere of soepelere kassa maakt storten makkelijker. Stel daarom een stortlimiet in voordat je begint, en schrijf je in bij Cruks als je een pauze wilt. Gokken is alleen voor 18+.",
    },
  ],
  sources: [
    { label: "Kansspeltoezichthouder — Guidance verificatie bankrekeningen (8 oktober 2026)", url: "https://kansspelautoriteit.nl/ksa-publiceert-guidance-verificatie-bankrekeningen" },
    { label: "Betaalvereniging Nederland — IBAN-Naam Check", url: "https://www.betaalvereniging.nl/kennisbank/iban-en-bic/iban-naam-check/" },
  ],
  related: [
    { label: "iDIN-verificatie uitgelegd", path: "/idin-casinos" },
    { label: "Snel registreren zonder iDIN", path: "/online-casino-zonder-idin" },
    { label: "Storten met iDEAL", path: "/betaalmethoden/ideal" },
  ],
};
